import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, useInView, useAnimation, PanInfo } from 'framer-motion';
import { CalendarIcon, TrendingUpIcon, AwardIcon, MapPinIcon, ChevronLeft, ChevronRight } from 'lucide-react';
interface TimelineStep {
  id: number;
  title: string;
  subtitle: string;
  description: string;
  metric: {
    value: number;
    label: string;
    prefix?: string;
    suffix?: string;
  };
  icon: ComponentType<{
    className?: string;
  }>;
  color: string;
}
const steps: TimelineStep[] = [{
  id: 1,
  title: 'Ziua 1',
  subtitle: 'Primul comision',
  description: 'Începe trainingul și primești acces la sistem. Primul comision poate veni chiar în prima zi dacă aplici imediat ce înveți.',
  metric: {
    value: 1,
    label: 'Zi până la primul rezultat',
    suffix: ' zi'
  },
  icon: CalendarIcon,
  color: 'from-[#357BEB] to-[#2868d8]'
}, {
  id: 2,
  title: 'Luna 1',
  subtitle: 'Primele rezultate',
  description: 'În prima lună, agenții activi realizează în medie 3-5 tranzacții de închiriere. Recordul este de 16 tranzacții în prima lună.',
  metric: {
    value: 4,
    label: 'Tranzacții medii în luna 1',
    suffix: ' tranzacții'
  },
  icon: TrendingUpIcon,
  color: 'from-[#2868d8] to-[#1e5bb8]'
}, {
  id: 3,
  title: 'Luna 3',
  subtitle: 'Stabilitate',
  description: 'După 3 luni de activitate constantă, devii Selling Closer și primești acces la liste de proprietari pentru vânzări.',
  metric: {
    value: 3000,
    label: 'Comision potențial pe fiecare tranzacție de vânzare',
    prefix: '€',
    suffix: ''
  },
  icon: AwardIcon,
  color: 'from-[#1e5bb8] to-[#132C53]'
}, {
  id: 4,
  title: 'Luna 6+',
  subtitle: 'Performanță maximă',
  description: 'Agenții care aplică constant metodologia ajung la 10+ tranzacții și câștigă comisioane de 5.000-10.000€ lunar.',
  metric: {
    value: 8000,
    label: 'Comision lunar mediu',
    prefix: '€',
    suffix: '+'
  },
  icon: TrendingUpIcon,
  color: 'from-[#132C53] to-[#21A366]'
}];
function AnimatedCounter({
  value,
  prefix = '',
  suffix = '',
  duration = 2
}: {
  value: number;
  prefix?: string;
  suffix?: string;
  duration?: number;
}) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true
  });
  useEffect(() => {
    if (!isInView) return;
    let startTime: number;
    let animationFrame: number;
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * value));
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        setCount(value);
      }
    };
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [isInView, value, duration]);
  return <div ref={ref} className="text-4xl sm:text-5xl md:text-6xl font-bold" data-id="element-6828">
      {prefix}
      {count.toLocaleString()}
      {suffix}
    </div>;
}
export function TimelineResultsSection() {
  const [activeStep, setActiveStep] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isManualSelection, setIsManualSelection] = useState(false);
  const [showHint, setShowHint] = useState(true);
  const sectionRef = useRef<HTMLDivElement>(null);
  const controls = useAnimation();
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const sectionTop = rect.top;
      const sectionHeight = rect.height;
      const windowHeight = window.innerHeight;
      const sectionStart = sectionTop + windowHeight * 0.3;
      const sectionEnd = sectionTop - windowHeight * 0.7;
      if (sectionTop < windowHeight && sectionTop + sectionHeight > 0) {
        let progress = 0;
        if (sectionTop > sectionStart) {
          progress = 0;
        } else if (sectionTop < sectionEnd) {
          progress = 1;
        } else {
          progress = (sectionStart - sectionTop) / (sectionStart - sectionEnd);
        }
        progress = Math.max(0, Math.min(1, progress));
        setScrollProgress(progress);
        // Only update activeStep based on scroll if user hasn't made a manual selection
        if (!isManualSelection) {
          const stepIndex = Math.min(Math.floor(progress * steps.length), steps.length - 1);
          setActiveStep(stepIndex);
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isManualSelection]);
  // Hide hint after first interaction
  useEffect(() => {
    if (isManualSelection && showHint) {
      setTimeout(() => setShowHint(false), 500);
    }
  }, [isManualSelection, showHint]);
  const handleStepClick = (index: number) => {
    setActiveStep(index);
    setIsManualSelection(true);
    controls.start({
      scale: [1, 1.05, 1],
      transition: {
        duration: 0.3
      }
    });
  };
  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      handleStepClick(activeStep + 1);
    }
  };
  const handlePrevious = () => {
    if (activeStep > 0) {
      handleStepClick(activeStep - 1);
    }
  };
  const handleDragEnd = (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const swipeThreshold = 50;
    if (info.offset.x > swipeThreshold && activeStep > 0) {
      handlePrevious();
    } else if (info.offset.x < -swipeThreshold && activeStep < steps.length - 1) {
      handleNext();
    }
  };
  const currentStep = steps[activeStep];
  const progressPercentage = isManualSelection ? activeStep / (steps.length - 1) * 100 : scrollProgress * 100;
  return <section ref={sectionRef} className="w-full bg-gradient-to-br from-gray-50 to-white py-12 sm:py-16 md:py-20 lg:py-24" data-id="element-6829">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-6830">
        {/* Header - MOBILE OPTIMIZED */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-8 sm:mb-12 md:mb-16" data-id="element-6831">
          <div className="inline-block bg-[#357BEB] bg-opacity-10 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-semibold text-[#357BEB] mb-3 sm:mb-4 md:mb-6" data-id="element-6832">
            📊 Parcursul tău către succes
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#132C53] mb-3 sm:mb-4 md:mb-6 px-4 leading-tight" data-id="element-6833">
            De la zero la rezultate reale
          </h2>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4" data-id="element-6834">
            Urmărește evoluția ta în primele 6 luni de activitate
          </p>
        </motion.div>

        {/* Animated hint - MOBILE OPTIMIZED */}
        {showHint && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} className="text-center mb-4 sm:mb-6" data-id="element-6835">
            <motion.div animate={{
          y: [0, -8, 0]
        }} transition={{
          duration: 1.5,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="inline-flex items-center gap-2 bg-[#357BEB] text-white px-4 py-2 sm:px-6 sm:py-3 rounded-full text-xs sm:text-sm md:text-base font-semibold shadow-lg" data-id="element-6836">
              <span data-id="element-6837">👇</span>
              <span className="hidden sm:inline" data-id="element-6838">
                Apasă pe etape sau folosește săgețile
              </span>
              <span className="sm:hidden" data-id="element-6839">Apasă pe etape</span>
              <span data-id="element-6840">👇</span>
            </motion.div>
          </motion.div>}

        {/* Progress bar - MOBILE OPTIMIZED */}
        <div className="mb-6 sm:mb-8 md:mb-12" data-id="element-6841">
          <div className="flex items-center justify-between mb-3 sm:mb-4 px-2" data-id="element-6842">
            <div className="text-xs sm:text-sm font-semibold text-gray-500" data-id="element-6843">
              Progresul tău
            </div>
            <div className="text-xs sm:text-sm font-medium text-[#357BEB]" data-id="element-6844">
              Etapa {activeStep + 1} din {steps.length}
            </div>
          </div>
          <div className="relative px-2" data-id="element-6845">
            <div className="absolute inset-0 h-2 sm:h-3 bg-gray-200 rounded-full" data-id="element-6846"></div>
            <motion.div className="absolute inset-0 h-2 sm:h-3 rounded-full" style={{
            background: `linear-gradient(to right, #357BEB, #21A366)`
          }} initial={{
            width: '0%'
          }} animate={{
            width: `${progressPercentage}%`
          }} transition={{
            duration: 0.3,
            ease: 'easeOut'
          }} data-id="element-6847" />
            <div className="relative flex justify-between" data-id="element-6848">
              {steps.map((step, index) => {
              const isActive = index === activeStep;
              const isPassed = scrollProgress > index / steps.length;
              return <motion.button key={step.id} onClick={() => handleStepClick(index)} className="relative group cursor-pointer focus:outline-none focus:ring-2 sm:focus:ring-4 focus:ring-[#357BEB] focus:ring-opacity-50 rounded-full" whileHover={{
                scale: 1.1
              }} whileTap={{
                scale: 0.95
              }} animate={isActive && showHint ? {
                scale: [1, 1.1, 1]
              } : {}} transition={{
                scale: {
                  repeat: isActive && showHint ? Infinity : 0,
                  duration: 2,
                  ease: 'easeInOut'
                }
              }} data-id="element-6849">
                    {/* Larger clickable area - MOBILE OPTIMIZED */}
                    <div className="p-1.5 sm:p-2 md:p-3" data-id="element-6850">
                      <div className={`w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 rounded-full border-2 sm:border-3 md:border-4 transition-all shadow-lg ${isActive ? 'bg-[#357BEB] border-white scale-110 shadow-2xl ring-2 sm:ring-4 ring-[#357BEB] ring-opacity-40' : isPassed ? 'bg-[#21A366] border-white hover:ring-2 sm:hover:ring-4 hover:ring-[#21A366] hover:ring-opacity-40' : 'bg-white border-gray-300 hover:border-[#357BEB] hover:shadow-xl hover:bg-[#357BEB] hover:bg-opacity-5'}`} data-id="element-6851">
                        <div className="w-full h-full flex items-center justify-center" data-id="element-6852">
                          <step.icon className={`w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 lg:w-7 lg:h-7 transition-colors ${isActive || isPassed ? 'text-white' : 'text-gray-400 group-hover:text-[#357BEB]'}`} data-id="element-6853" />
                        </div>
                      </div>
                    </div>
                    <div className="absolute top-full mt-1 sm:mt-2 left-1/2 transform -translate-x-1/2 whitespace-nowrap" data-id="element-6854">
                      <div className={`text-[10px] sm:text-xs md:text-sm font-bold transition-colors ${isActive ? 'text-[#357BEB]' : 'text-gray-500 group-hover:text-[#357BEB]'}`} data-id="element-6855">
                        {step.title}
                      </div>
                    </div>
                  </motion.button>;
            })}
            </div>
          </div>
        </div>

        {/* Main content card - MOBILE OPTIMIZED */}
        <motion.div key={activeStep} initial={{
        opacity: 0,
        x: 50
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -50
      }} transition={{
        duration: 0.4
      }} drag="x" dragConstraints={{
        left: 0,
        right: 0
      }} dragElastic={0.2} onDragEnd={handleDragEnd} className="bg-white rounded-xl sm:rounded-2xl md:rounded-3xl p-4 sm:p-6 md:p-8 lg:p-12 shadow-2xl border-2 border-gray-100 cursor-grab active:cursor-grabbing" data-id="element-6856">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 md:gap-12 items-center" data-id="element-6857">
            <div className="space-y-3 sm:space-y-4 md:space-y-6" data-id="element-6858">
              <div className={`inline-block bg-gradient-to-r ${currentStep.color} text-white px-3 py-1.5 sm:px-4 sm:py-2 md:px-6 md:py-3 rounded-lg sm:rounded-xl md:rounded-2xl font-bold text-xs sm:text-sm md:text-base lg:text-lg shadow-lg`} data-id="element-6859">
                {currentStep.subtitle}
              </div>
              <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-[#132C53] leading-tight" data-id="element-6860">
                {currentStep.title}
              </h3>
              <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 leading-relaxed" data-id="element-6861">
                {currentStep.description}
              </p>
              <div className="flex items-center space-x-3 sm:space-x-4 pt-2 sm:pt-4" data-id="element-6862">
                <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#357BEB] rounded-full" data-id="element-6863"></div>
                <div className="text-xs sm:text-sm font-semibold text-gray-500" data-id="element-6864">
                  Etapa {activeStep + 1} din {steps.length}
                </div>
              </div>
            </div>
            <motion.div animate={controls} className="bg-gradient-to-br from-[#357BEB] to-[#132C53] rounded-xl sm:rounded-2xl md:rounded-3xl p-6 sm:p-8 md:p-10 lg:p-12 text-white text-center shadow-xl" data-id="element-6865">
              <div className="mb-3 sm:mb-4 md:mb-6" data-id="element-6866">
                <currentStep.icon className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 mx-auto text-white opacity-80" data-id="element-6867" />
              </div>
              <AnimatedCounter value={currentStep.metric.value} prefix={currentStep.metric.prefix} suffix={currentStep.metric.suffix} data-id="element-6868" />
              <div className="text-sm sm:text-base md:text-lg lg:text-xl font-semibold mt-2 sm:mt-3 md:mt-4 text-gray-200 px-2" data-id="element-6869">
                {currentStep.metric.label}
              </div>
              <div className="mt-4 sm:mt-6 md:mt-8 pt-4 sm:pt-6 md:pt-8 border-t border-white border-opacity-20" data-id="element-6870">
                <div className="text-xs sm:text-sm text-gray-300" data-id="element-6871">
                  Bazat pe date reale din ultimele 12 luni
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Navigation buttons - MOBILE OPTIMIZED */}
        <div className="flex items-center justify-center gap-2 sm:gap-3 md:gap-4 mt-6 sm:mt-8" data-id="element-6872">
          <motion.button onClick={handlePrevious} disabled={activeStep === 0} whileHover={{
          scale: activeStep === 0 ? 1 : 1.05
        }} whileTap={{
          scale: activeStep === 0 ? 1 : 0.95
        }} className={`flex items-center gap-1.5 sm:gap-2 px-3 py-2 sm:px-4 sm:py-2.5 md:px-6 md:py-3 rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base md:text-lg transition-all shadow-lg ${activeStep === 0 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-white text-[#357BEB] hover:bg-[#357BEB] hover:text-white border-2 border-[#357BEB]'}`} data-id="element-6873">
            <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-6874" />
            <span className="hidden sm:inline" data-id="element-6875">Anterior</span>
          </motion.button>

          <div className="flex gap-1.5 sm:gap-2" data-id="element-6876">
            {steps.map((_, index) => <button key={index} onClick={() => handleStepClick(index)} className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full transition-all ${index === activeStep ? 'bg-[#357BEB] w-6 sm:w-8' : 'bg-gray-300 hover:bg-gray-400'}`} aria-label={`Go to step ${index + 1}`} data-id="element-6877" />)}
          </div>

          <motion.button onClick={handleNext} disabled={activeStep === steps.length - 1} whileHover={{
          scale: activeStep === steps.length - 1 ? 1 : 1.05
        }} whileTap={{
          scale: activeStep === steps.length - 1 ? 1 : 0.95
        }} className={`flex items-center gap-1.5 sm:gap-2 px-3 py-2 sm:px-4 sm:py-2.5 md:px-6 md:py-3 rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base md:text-lg transition-all shadow-lg ${activeStep === steps.length - 1 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-[#357BEB] text-white hover:bg-[#2868d8] border-2 border-[#357BEB]'}`} data-id="element-6878">
            <span className="hidden sm:inline" data-id="element-6879">Următorul</span>
            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-6880" />
          </motion.button>
        </div>

        {/* Mobile swipe hint - MOBILE OPTIMIZED */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: showHint ? 1 : 0
      }} className="mt-4 sm:mt-6 text-center sm:hidden" data-id="element-6881">
          <p className="text-xs sm:text-sm text-gray-500 flex items-center justify-center gap-2" data-id="element-6882">
            <span data-id="element-6883">👈</span>
            <span data-id="element-6884">Glisează pentru a naviga</span>
            <span data-id="element-6885">👉</span>
          </p>
        </motion.div>
      </div>
    </section>;
}